import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRecipe } from '../../contexts/RecipeContext';
import { FaEllipsisV, FaSave, FaTrash } from 'react-icons/fa';
import StarRating from '../StarRating';
import '../../styles/recipes/RecipeForm.css';

// Available units for ingredients
const UNITS = ['g', 'kg', 'ml', 'l', 'Stück', 'TL', 'EL', 'Tasse', 'Prise', 'Packung', 'Dose', 'Stange', 'Bund', 'Scheibe', 'Zehe'];

// Difficulty levels
const DIFFICULTIES = ['easy', 'medium', 'hard'];

// Extracts the leading minute count from a time value (e.g. "15 min" -> "15",
// 15 -> "15", "" -> ""). Used so the prep/cook time inputs can be plain number
// fields while still round-tripping the backend's "X min" string format.
const extractMinutes = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const match = String(value).match(/\d+/);
  return match ? match[0] : '';
};

// Formats a plain minute-count input value back into the "X min" string the
// backend parser expects (empty stays empty, not "0 min" - see applyDtoToEntity
// on the backend, which now leaves the existing value untouched on empty/null).
const formatMinutes = (value) => {
  const digits = extractMinutes(value);
  return digits === '' ? '' : `${digits} min`;
};

const RecipeForm = ({ recipe, bookmarkId, onSubmit, onCancel, onDelete }) => {
  const { t } = useLanguage();
  const { createRecipe, updateRecipe, deleteRecipe } = useRecipe();

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    baseServings: 4,
    preparationTime: '',
    cookingTime: '',
    difficulty: 'medium',
    tags: [],
    ingredients: [],
    steps: [],
    rating: 0
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  // Ref to access latest formData in cleanup/callbacks
  const formDataRef = useRef(formData);
  const hasChangesRef = useRef(hasChanges);
  const menuRef = useRef(null);
  const isEditMode = !!recipe;

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [showMenu]);

  // Keep refs in sync
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    hasChangesRef.current = hasChanges;
  }, [hasChanges]);

  // Populate form for edit mode
  useEffect(() => {
    if (recipe) {
      setFormData({
        title: recipe.title || '',
        description: recipe.description || '',
        baseServings: recipe.baseServings || 4,
        preparationTime: extractMinutes(recipe.preparationTime),
        cookingTime: extractMinutes(recipe.cookingTime),
        difficulty: recipe.difficulty || 'medium',
        tags: recipe.tags || [],
        ingredients: recipe.ingredients || [],
        steps: recipe.steps || [],
        rating: recipe.rating || 0
      });
      setHasChanges(false);
    }
  }, [recipe]);

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'baseServings' ? (parseInt(value) || 1)
        : (name === 'preparationTime' || name === 'cookingTime') ? value.replace(/[^0-9]/g, '')
        : value
    }));
    setHasChanges(true);

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Add new ingredient
  const addIngredient = () => {
    const newIngredient = {
      id: `ing-${Date.now()}`,
      name: '',
      quantity: 1,
      unit: 'Stück',
      note: ''
    };
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, newIngredient]
    }));
    setHasChanges(true);
  };

  // Remove ingredient
  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  // Update ingredient field
  const updateIngredient = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) =>
        i === index ? { ...ing, [field]: field === 'quantity' ? parseFloat(value) || 0 : value } : ing
      )
    }));
    setHasChanges(true);
  };

  // Add new preparation step
  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, '']
    }));
    setHasChanges(true);
  };

  // Remove preparation step
  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  // Update preparation step text
  const updateStep = (index, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => i === index ? value : step)
    }));
    setHasChanges(true);
  };

  // Move a preparation step up/down (reorder)
  const moveStep = (index, direction) => {
    setFormData(prev => {
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= prev.steps.length) return prev;
      const steps = [...prev.steps];
      [steps[index], steps[newIndex]] = [steps[newIndex], steps[index]];
      return { ...prev, steps };
    });
    setHasChanges(true);
  };

  // Validation
  const validateForm = (data = formData) => {
    const newErrors = {};

    if (!data.title.trim()) {
      newErrors.title = t('recipes.form.titleRequired');
    }

    if (data.baseServings < 1) {
      newErrors.baseServings = t('recipes.form.servingsInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Auto-save helper (without closing)
  const performAutoSave = useCallback(async () => {
    const currentFormData = formDataRef.current;
    const currentHasChanges = hasChangesRef.current;

    // Only auto-save in edit mode with changes and valid title
    if (isEditMode && currentHasChanges && currentFormData.title.trim() && recipe?.id) {
      try {
        const payload = {
          ...currentFormData,
          bookmarkId: recipe?.bookmarkId || `manual-${Date.now()}`,
          title: currentFormData.title.trim(),
          description: currentFormData.description.trim() || null,
          preparationTime: formatMinutes(currentFormData.preparationTime),
          cookingTime: formatMinutes(currentFormData.cookingTime),
          ingredients: currentFormData.ingredients.filter(ing => ing.name.trim() !== ''),
          steps: currentFormData.steps.map(step => step.trim()).filter(step => step !== '')
        };
        await updateRecipe(recipe.id, payload);
        return true;
      } catch (error) {
        console.error('Auto-save error:', error);
        return false;
      }
    }
    return false;
  }, [isEditMode, recipe, updateRecipe]);

  // Auto-save and close (for edit mode)
  const autoSaveAndClose = useCallback(async () => {
    await performAutoSave();
    onCancel && onCancel();
  }, [performAutoSave, onCancel]);

  // Auto-save on unmount (when navigating away)
  useEffect(() => {
    return () => {
      // Fire and forget - component is unmounting
      if (isEditMode && hasChangesRef.current && formDataRef.current.title.trim() && recipe?.id) {
        const payload = {
          ...formDataRef.current,
          bookmarkId: recipe?.bookmarkId || `manual-${Date.now()}`,
          title: formDataRef.current.title.trim(),
          description: formDataRef.current.description.trim() || null,
          preparationTime: formatMinutes(formDataRef.current.preparationTime),
          cookingTime: formatMinutes(formDataRef.current.cookingTime),
          ingredients: formDataRef.current.ingredients.filter(ing => ing.name.trim() !== ''),
          steps: formDataRef.current.steps.map(step => step.trim()).filter(step => step !== '')
        };
        // Use the updateRecipe from context - it handles the async
        updateRecipe(recipe.id, payload).catch(err => console.error('Auto-save on unmount failed:', err));
      }
    };
  }, [isEditMode, recipe, updateRecipe]);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        bookmarkId: bookmarkId || recipe?.bookmarkId || `manual-${Date.now()}`,
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        preparationTime: formatMinutes(formData.preparationTime),
        cookingTime: formatMinutes(formData.cookingTime),
        ingredients: formData.ingredients.filter(ing => ing.name.trim() !== ''),
        steps: formData.steps.map(step => step.trim()).filter(step => step !== '')
      };

      if (recipe) {
        await updateRecipe(recipe.id, payload);
      } else {
        await createRecipe(payload);
      }

      onSubmit && onSubmit();
    } catch (error) {
      console.error('Error saving recipe:', error);
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!window.confirm(t('recipes.form.confirmDelete'))) return;

    setIsSubmitting(true);
    try {
      await deleteRecipe(recipe.id);
      onDelete && onDelete();
    } catch (error) {
      console.error('Error deleting recipe:', error);
      setErrors({ submit: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dynamic-panel recipe-form-panel">
      <div className="panel-header">
        <h2>{recipe ? t('recipes.form.editTitle') : t('recipes.form.createTitle')}</h2>
        <button
          type="button"
          className="close-btn"
          onClick={isEditMode ? autoSaveAndClose : onCancel}
          title={isEditMode ? t('recipes.form.saveAndClose') : t('cancel')}
        >
          ✕
        </button>
      </div>

      <div className="panel-content">
        <form className="recipe-form" onSubmit={handleSubmit}>
          {/* Error Message */}
          {errors.submit && (
            <div className="error-message" style={{ color: '#dc3545', marginBottom: '16px' }}>
              {errors.submit}
            </div>
          )}

          {/* Title */}
          <div className="form-group">
            <div className="form-label-row">
              <label htmlFor="recipe-title">{t('recipes.form.title')} *</label>
              <div className="form-overflow-container" ref={menuRef}>
                <button
                  type="button"
                  className="form-overflow-btn"
                  onClick={() => setShowMenu(!showMenu)}
                  aria-label="Menü"
                >
                  <FaEllipsisV />
                </button>
                {showMenu && (
                  <div className="form-overflow-menu">
                    <button
                      type="submit"
                      className="overflow-menu-item"
                      disabled={isSubmitting}
                    >
                      <FaSave />
                      <span>{isSubmitting ? t('saving') : (isEditMode ? t('save') : t('recipes.form.create'))}</span>
                    </button>
                    {isEditMode && (
                      <button
                        type="button"
                        className="overflow-menu-item overflow-menu-item-danger"
                        onClick={handleDelete}
                        disabled={isSubmitting}
                      >
                        <FaTrash />
                        <span>{t('delete')}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            <input
              type="text"
              id="recipe-title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder={t('recipes.form.title')}
              className={errors.title ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.title && <span className="field-error">{errors.title}</span>}
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="recipe-description">{t('recipes.form.description')}</label>
            <textarea
              id="recipe-description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t('recipes.form.description')}
              rows="3"
              disabled={isSubmitting}
            />
          </div>

          {/* Base Servings & Difficulty */}
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="recipe-servings">{t('recipes.form.baseServings')} *</label>
                <input
                  type="number"
                  id="recipe-servings"
                  name="baseServings"
                  min="1"
                  max="50"
                  value={formData.baseServings}
                  onChange={handleInputChange}
                  className={errors.baseServings ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {errors.baseServings && <span className="field-error">{errors.baseServings}</span>}
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="recipe-difficulty">{t('recipes.form.difficulty')}</label>
                <select
                  id="recipe-difficulty"
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                >
                  {DIFFICULTIES.map(d => (
                    <option key={d} value={d}>{t(`recipes.difficulty.${d}`)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Preparation & Cooking Time */}
          <div className="form-row">
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="recipe-preptime">{t('recipes.form.prepTime')} ({t('recipes.form.minutes') || 'Minuten'})</label>
                <input
                  type="number"
                  id="recipe-preptime"
                  name="preparationTime"
                  min="0"
                  max="1440"
                  placeholder="15"
                  value={formData.preparationTime}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="form-col">
              <div className="form-group">
                <label htmlFor="recipe-cooktime">{t('recipes.form.cookTime')} ({t('recipes.form.minutes') || 'Minuten'})</label>
                <input
                  type="number"
                  id="recipe-cooktime"
                  name="cookingTime"
                  min="0"
                  max="1440"
                  placeholder="45"
                  value={formData.cookingTime}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Ingredients Section */}
          <div className="form-group ingredients-section">
            <label>{t('recipes.ingredients')} ({formData.ingredients.length})</label>

            <div className="ingredients-list">
              {formData.ingredients.map((ing, index) => (
                <div key={ing.id || index} className="ingredient-row">
                  <input
                    type="number"
                    className="ingredient-quantity"
                    value={ing.quantity}
                    onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                    min="0"
                    step="0.1"
                    disabled={isSubmitting}
                  />
                  <select
                    className="ingredient-unit"
                    value={ing.unit}
                    onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                    disabled={isSubmitting}
                  >
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                  <input
                    type="text"
                    className="ingredient-name"
                    placeholder={t('recipes.form.ingredientName')}
                    value={ing.name}
                    onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                    disabled={isSubmitting}
                  />
                  <input
                    type="text"
                    className="ingredient-note"
                    placeholder={t('recipes.form.note')}
                    value={ing.note || ''}
                    onChange={(e) => updateIngredient(index, 'note', e.target.value)}
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="btn-remove-ingredient"
                    onClick={() => removeIngredient(index)}
                    disabled={isSubmitting}
                    title={t('delete')}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="add-ingredient-btn"
              onClick={addIngredient}
              disabled={isSubmitting}
            >
              + {t('recipes.form.addIngredient')}
            </button>
          </div>

          {/* Preparation Steps Section */}
          <div className="form-group steps-section">
            <label>{t('recipes.instructions') || 'Zubereitung'} ({formData.steps.length})</label>

            <div className="steps-list">
              {formData.steps.map((step, index) => (
                <div key={index} className="step-row">
                  <span className="step-number">{index + 1}</span>
                  <textarea
                    className="step-text"
                    placeholder={t('recipes.stepPlaceholder') || 'Zubereitungsschritt beschreiben...'}
                    value={step}
                    onChange={(e) => updateStep(index, e.target.value)}
                    rows="2"
                    disabled={isSubmitting}
                  />
                  <div className="step-actions">
                    <button
                      type="button"
                      className="btn-move-step"
                      onClick={() => moveStep(index, -1)}
                      disabled={isSubmitting || index === 0}
                      title={t('moveUp') || 'Nach oben'}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn-move-step"
                      onClick={() => moveStep(index, 1)}
                      disabled={isSubmitting || index === formData.steps.length - 1}
                      title={t('moveDown') || 'Nach unten'}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="btn-remove-step"
                      onClick={() => removeStep(index)}
                      disabled={isSubmitting}
                      title={t('recipes.removeStep') || 'Schritt entfernen'}
                    >
                      ✕
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="add-step-btn"
              onClick={addStep}
              disabled={isSubmitting}
            >
              + {t('recipes.addStep') || 'Schritt hinzufügen'}
            </button>
          </div>

          {/* Star Rating Section */}
          <div className="form-group">
            <label htmlFor="recipe-rating">{t('rating') || 'Bewertung'}</label>
            <StarRating
              value={formData.rating}
              onChange={(newRating) => {
                setFormData(prev => ({ ...prev, rating: newRating }));
                setHasChanges(true);
              }}
              readOnly={false}
              maxStars={4}
              size="large"
              showEmpty={true}
            />
          </div>

        </form>
      </div>
    </div>
  );
};

export default RecipeForm;
